import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  type KeyboardEvent,
  type MouseEvent,
} from "react";

import {
  FileText,
  Database,
  UserCheck,
  UserX,
  MoreVertical,
  Trash2,
  MapPin,
  SquarePen,
  Plus,
  FilterX,
  ExternalLink,
  Download,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import AddAccount from "./AddAccount";
import DeleteAccountModal from "./DeleteAccountModal";
import Accordion from "../../Components/Accordion";
import AccountMetricsRow from "../../Components/AccountMetricsRow";
import InitialAvatar from "../../Components/Avatar";
import Button from "../../Components/Button";
import CustomCheckbox from "../../Components/CheckCircle";
import DropdownSelector from "../../Components/DropdownSelector";
import CommonInput from "../../Components/Input";
import CommonModal from "../../Components/Modal";
import SearchInput from "../../Components/Search/SearchInput";
import Select from "../../Components/Select";
import SidebarModal from "../../Components/SidebarModal";
import TabsComponent from "../../Components/Tab";
import Table from "../../Components/Table";
import ToggleSwitch from "../../Components/ToggleSwitch";
import { formatPublishedAlignmentLevel } from "../../Pages/Relationship/account-relationships/relationshipUtils";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchOrganizationLevels } from "../../Store/Reducers/AdminSlice";
import {
  fetchAccountsWithSponsors,
  fetchWatchLists,
  createWatchList,
  deleteWatchList,
  clearCreateWatchListError,
  clearCreateWatchListSuccess,
  clearDeleteAccountsBulkError,
  clearDeleteAccountsBulkSuccess,
  exportAccountsData,
  ActiveCustomerWithSponsorsItem,
  FetchAccountsFilters,
  WatchListItem,
  WatchListCustomer,
  AccountsExportScope,
} from "../../Store/Reducers/OpsSlice";
import { regionOptions } from "../../Utils/Constants/Constants";
import {
  getTierBadgeClasses,
  useAccountTiers,
} from "../../Utils/Helper/accountTierHelpers";
import { formatCompactCurrency } from "../../Utils/Helper/formatCompactCurrency";
import { formatDateTime, getInitials } from "../../Utils/Helper/Helper";
import { getAccountLevelCompactLabel } from "../../Utils/Helper/levelUtils";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../Utils/Helper/LocalStorage";
import { toast } from "../../Utils/Helper/toast";
import AccountsDetailsModal, { DetailField } from "../AccountsDetailsModal";
import DeleteModal from "../DeleteModal";

import "./style.scss";

const formatAccountRevenue = (
  revenue: number | string | null | undefined
): string =>
  formatCompactCurrency(revenue, { fallback: "$0", zeroValue: "$0" });

const matchesAccountSearch = (
  account: {
    name?: string;
    primarySponsor?: string;
    secondarySponsor?: string;
    primarySponsors?: string;
    secondarySponsors?: string;
  },
  searchQuery: string
): boolean => {
  const query = searchQuery.toLowerCase().trim();
  if (!query) return true;

  return [
    account.name,
    account.primarySponsor,
    account.secondarySponsor,
    account.primarySponsors,
    account.secondarySponsors,
  ].some(field =>
    String(field ?? "")
      .toLowerCase()
      .includes(query)
  );
};

const isEmptySponsorValue = (value?: string): boolean => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return (
    !normalized ||
    normalized === "n/a" ||
    normalized === "-" ||
    normalized === "unassigned"
  );
};

/** Match table-tab semantics: assigned if any primary/secondary sponsor is set. */
const isGroupedAccountAssigned = (account: {
  primarySponsor?: string;
  secondarySponsor?: string;
  primarySponsors?: string;
  secondarySponsors?: string;
}): boolean =>
  !isEmptySponsorValue(account.primarySponsor) ||
  !isEmptySponsorValue(account.secondarySponsor) ||
  !isEmptySponsorValue(account.primarySponsors) ||
  !isEmptySponsorValue(account.secondarySponsors);

const matchesAccountAssignmentFilter = (
  account: {
    primarySponsor?: string;
    secondarySponsor?: string;
    primarySponsors?: string;
    secondarySponsors?: string;
  },
  activeAccountFilter: string | null
): boolean => {
  if (!activeAccountFilter) return true;
  const isAssigned = isGroupedAccountAssigned(account);
  if (activeAccountFilter === "assigned") return isAssigned;
  if (activeAccountFilter === "unassigned") return !isAssigned;
  return true;
};

const AccountsContainer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const { tierOptions, formatTier } = useAccountTiers();

  const withOrg = useCallback(
    (path: string) => {
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return orgSlug ? `/${orgSlug}${normalizedPath}` : normalizedPath;
    },
    [orgSlug]
  );
  const { data: accountsDataResponse, loading: accountsLoading } =
    useAppSelector(state => state.ops.accounts);
  const { data: watchListsData, loading: watchListsLoading } = useAppSelector(
    state => state.ops.watchLists
  );
  const { loading: createWatchListLoading } = useAppSelector(
    state => state.ops.createWatchList
  );
  const { loading: deleteWatchListLoading } = useAppSelector(
    state => state.ops.deleteWatchList
  );
  const { organizationLevels } = useAppSelector((state: any) => state.admin);
  const accountsExportLoading = useAppSelector(
    state => state.ops.accountsExport.loading
  );

  const [activeAccountFilter, setActiveAccountFilter] = useState<string | null>(
    ""
  );
  const [showByGroup, setShowByGroup] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFilter, setExportFilter] = useState<AccountsExportScope>("all");
  const [exportFormat, setExportFormat] = useState<"csv" | "excel">("csv");
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [createGroupStep, setCreateGroupStep] = useState<1 | 2>(1);
  const [groupName, setGroupName] = useState("");
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false);
  const [accountToEdit, setAccountToEdit] =
    useState<ActiveCustomerWithSponsorsItem | null>(null);
  const [viewAccount, setViewAccount] =
    useState<ActiveCustomerWithSponsorsItem | null>(null);
  const [accountToDelete, setAccountToDelete] =
    useState<ActiveCustomerWithSponsorsItem | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<WatchListItem | null>(
    null
  );
  const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false);
  const [accountDetailsFields, setAccountDetailsFields] = useState<
    DetailField[]
  >([]);
  const [isAccountDetailsModalOpen, setIsAccountDetailsModalOpen] =
    useState(false);

  // Get user ID from localStorage
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const userId = loggedInUser?.userId;

  // Fetch accounts when filters change
  useEffect(() => {
    const filters: FetchAccountsFilters = {
      assigned_status:
        activeAccountFilter === ""
          ? null
          : (activeAccountFilter as "assigned" | "unassigned" | null),
      tier: selectedTier,
      region: selectedRegion,
      level: selectedLevel,
    };

    // Remove undefined values
    Object.keys(filters).forEach(
      key =>
        filters[key as keyof FetchAccountsFilters] === undefined &&
        delete filters[key as keyof FetchAccountsFilters]
    );

    dispatch(fetchAccountsWithSponsors(filters));
  }, [
    dispatch,
    activeAccountFilter,
    selectedTier,
    selectedRegion,
    selectedLevel,
  ]);

  // Fetch watch lists when showByGroup is enabled or filters change
  useEffect(() => {
    if (showByGroup) {
      const filters: FetchAccountsFilters = {
        assigned_status:
          activeAccountFilter === ""
            ? null
            : (activeAccountFilter as "assigned" | "unassigned" | null),
        tier: selectedTier,
        region: selectedRegion,
        level: selectedLevel,
      };

      // Remove undefined values
      Object.keys(filters).forEach(
        key =>
          filters[key as keyof FetchAccountsFilters] === undefined &&
          delete filters[key as keyof FetchAccountsFilters]
      );

      dispatch(fetchWatchLists({ filters }));
    }
  }, [
    dispatch,
    showByGroup,
    activeAccountFilter,
    selectedTier,
    selectedRegion,
    selectedLevel,
  ]);

  // Fetch organization levels on mount
  useEffect(() => {
    if (organizationLevels.length === 0) {
      dispatch(fetchOrganizationLevels());
    }
  }, [dispatch, organizationLevels.length]);

  const handleAccountFilterChange = (tabId: string) => {
    setActiveAccountFilter(tabId || null);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setActiveAccountFilter("");
    setSelectedTier(null);
    setSelectedRegion(null);
    setSelectedLevel("");
  };

  const handleOpenExportModal = () => {
    setExportFilter("all");
    setExportFormat("csv");
    setIsExportModalOpen(true);
  };

  const handleCloseExportModal = () => {
    setIsExportModalOpen(false);
  };

  const handleConfirmExport = async () => {
    try {
      const filename = await dispatch(
        exportAccountsData({ format: exportFormat, scope: exportFilter })
      ).unwrap();
      toast.success(`Exported ${filename}`);
      setIsExportModalOpen(false);
    } catch {
      // OpsSlice shows error toast on rejection
    }
  };

  const handleAccountSelect = (accountId: string) => {
    setSelectedAccountIds(prev =>
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAccountIds.length === accountsData.length) {
      setSelectedAccountIds([]);
    } else {
      setSelectedAccountIds(accountsData.map(acc => acc.id));
    }
  };

  const handleCreateClick = () => {
    setCreateGroupStep(1);
    setSelectedAccountIds([]);
    setIsCreateGroupModalOpen(true);
  };

  const handleSaveGroup = async () => {
    if (
      groupName.trim() &&
      selectedAccountIds.length > 0 &&
      !isDuplicateGroupName
    ) {
      const result = await dispatch(
        createWatchList({
          watch_list_name: groupName.trim(),
          customer_ids: selectedAccountIds,
        })
      );

      if (result.meta.requestStatus === "fulfilled") {
        // Reset states
        setGroupName("");
        setCreateGroupStep(1);
        setIsCreateGroupModalOpen(false);
        setSelectedAccountIds([]);

        // Enable "Show By Group" to display the newly created group
        setShowByGroup(true);

        // Refresh watch lists with current filters
        if (userId) {
          const filters: FetchAccountsFilters = {
            assigned_status:
              activeAccountFilter === ""
                ? null
                : (activeAccountFilter as "assigned" | "unassigned" | null),
            tier: selectedTier,
            region: selectedRegion,
            level: selectedLevel,
          };

          // Remove undefined values
          Object.keys(filters).forEach(
            key =>
              filters[key as keyof FetchAccountsFilters] === undefined &&
              delete filters[key as keyof FetchAccountsFilters]
          );

          dispatch(fetchWatchLists({ filters }));
        }
      }
    }
  };

  const handleBackFromModal = () => {
    setIsCreateGroupModalOpen(false);
    setCreateGroupStep(1);
    setSelectedAccountIds([]);
    setGroupName("");
    dispatch(clearCreateWatchListError());
    dispatch(clearCreateWatchListSuccess());
  };

  const handleDeleteClick = (account: DisplayAccountData) => {
    const apiRecord = accountsDataRaw.find(
      item => item.customer_id === account.id
    );
    if (apiRecord) {
      setAccountToDelete(apiRecord);
      setIsDeleteModalOpen(true);
    }
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setAccountToDelete(null);
    dispatch(clearDeleteAccountsBulkError());
    dispatch(clearDeleteAccountsBulkSuccess());
  };

  const handleDeleteGroupClick = (
    event: MouseEvent | KeyboardEvent,
    watchList: WatchListItem
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setGroupToDelete(watchList);
    setIsDeleteGroupModalOpen(true);
  };

  const handleDeleteGroupModalClose = () => {
    if (deleteWatchListLoading) return;
    setIsDeleteGroupModalOpen(false);
    setGroupToDelete(null);
  };

  const handleDeleteGroupConfirm = async () => {
    if (!groupToDelete?.watch_list_id) return;

    const result = await dispatch(deleteWatchList(groupToDelete.watch_list_id));

    if (result.meta.requestStatus === "fulfilled") {
      setIsDeleteGroupModalOpen(false);
      setGroupToDelete(null);
    }
  };

  const handleDeleteSuccess = () => {
    // Refresh accounts list after successful deletion
    const filters: FetchAccountsFilters = {
      assigned_status:
        activeAccountFilter === ""
          ? null
          : (activeAccountFilter as "assigned" | "unassigned" | null),
      tier: selectedTier,
      region: selectedRegion,
      level: selectedLevel,
    };

    Object.keys(filters).forEach(
      key =>
        filters[key as keyof FetchAccountsFilters] === undefined &&
        delete filters[key as keyof FetchAccountsFilters]
    );

    dispatch(fetchAccountsWithSponsors(filters));
  };

  // Clear errors when modal opens
  useEffect(() => {
    if (isCreateGroupModalOpen) {
      dispatch(clearCreateWatchListError());
      dispatch(clearCreateWatchListSuccess());
    }
  }, [isCreateGroupModalOpen, dispatch]);

  const accountFilterTabs = useMemo(() => {
    const activeTabId = activeAccountFilter || "";
    const total = accountsDataResponse?.total;
    const activeTabCount =
      accountsLoading || total === undefined || total === null
        ? undefined
        : total;

    return [
      {
        id: "",
        label: "All Accounts",
        icon: <Database size={18} />,
        count: activeTabId === "" ? activeTabCount : undefined,
      },
      {
        id: "assigned",
        label: "Assigned Accounts",
        icon: <UserCheck size={18} />,
        count: activeTabId === "assigned" ? activeTabCount : undefined,
      },
      {
        id: "unassigned",
        label: "Not Assigned Accounts",
        icon: <UserX size={18} />,
        count: activeTabId === "unassigned" ? activeTabCount : undefined,
      },
    ];
  }, [activeAccountFilter, accountsDataResponse?.total, accountsLoading]);

  // Transform organization levels into dropdown options
  const levelOptions = useMemo(() => {
    const options = [{ label: "All Account Levels", value: "" }];

    if (organizationLevels && organizationLevels.length > 0) {
      organizationLevels.forEach(
        (level: { level_code: string; name: string }) =>
          options.push({
            label: getAccountLevelCompactLabel(level.level_code, level.name),
            value: level.level_code,
          })
      );
    }

    return options;
  }, [organizationLevels]);

  interface DisplayAccountData {
    id: string;
    name: string;
    initials: string;
    region: string;
    primarySponsor: string;
    secondarySponsor: string;
    tier: string;
    industry: string;
    location: string;
    alignmentLevel: string;
    revenue: string;
    renewal: string;
  }

  interface GroupedAccountData extends DisplayAccountData {
    primarySponsors?: string;
    secondarySponsors?: string;
  }

  // Helper function to get tier badge Tailwind classes based on tier value
  const renderTierBadge = (value: string) => {
    if (!value || value === "N/A") {
      return <span>{value}</span>;
    }
    const label = formatTier(value);
    return (
      <span
        title={label}
        className={`inline-block px-2 py-0.5 text-sm rounded-full font-normal text-center whitespace-nowrap ${getTierBadgeClasses(value)}`}
      >
        {label}
      </span>
    );
  };

  // Store raw API data
  const accountsDataRaw = useMemo(() => {
    if (!accountsDataResponse?.items) return [];
    return accountsDataResponse.items;
  }, [accountsDataResponse]);

  // Transform and filter accounts data for display
  const accountsData: DisplayAccountData[] = useMemo(() => {
    const transformedData = accountsDataRaw.map(item => {
      // Use API fields directly, with fallbacks
      const industry = item.industry || "N/A";
      const location =
        item.location || item.city || item.country || item.region || "N/A";

      return {
        id: item.customer_id,
        name: item.name || "N/A",
        initials: getInitials(item.name || ""),
        region: item.region || "N/A",
        primarySponsor: item.primary_sponsors || "N/A",
        secondarySponsor: item.secondary_sponsors || "N/A",
        tier: item.tiers || "N/A",
        industry,
        location,
        alignmentLevel: formatPublishedAlignmentLevel(item.level),
        revenue: formatAccountRevenue(item.revenue),
        renewal: item.renewal
          ? formatDateTime(item.renewal).date
          : item.last_level_date
            ? formatDateTime(item.last_level_date).date
            : "N/A",
      };
    });

    if (!searchQuery.trim()) return transformedData;
    return transformedData.filter(account =>
      matchesAccountSearch(account, searchQuery)
    );
  }, [accountsDataRaw, searchQuery]);

  // Map watch list customer to grouped account data format
  const mapWatchListCustomerToGroupedAccountData = (
    customer: WatchListCustomer
  ): GroupedAccountData => {
    return {
      id: customer.name || "", // Using name as id since customer_id is not in new response
      name: customer.name || "N/A",
      initials: getInitials(customer.name || ""),
      region: customer.Location || "N/A",
      primarySponsor: customer["Primary Sponsors"] || "N/A",
      secondarySponsor: customer["Secondary Sponsors"] || "N/A",
      tier: customer.tier || "N/A",
      industry: customer.Industry || "N/A",
      location: customer.Location || "N/A",
      alignmentLevel: "N/A", // Not in watch list response
      revenue: formatAccountRevenue(customer.Revenue),
      renewal: customer.Renewal || "N/A",
      primarySponsors: customer["Primary Sponsors"] || "N/A",
      secondarySponsors: customer["Secondary Sponsors"] || "N/A",
    };
  };

  // Watch-list API ignores assignment tabs; filter customers per group for the active tab
  const filteredWatchListGroups = useMemo(() => {
    if (!watchListsData?.length) return [];

    return watchListsData
      .map((watchList: WatchListItem) => {
        const filteredAccounts = watchList.customers
          .map((customer, index) => {
            const account = mapWatchListCustomerToGroupedAccountData(customer);
            account.id = `${watchList.watch_list_id}-${customer.name || index}`;
            return account;
          })
          .filter(
            account =>
              matchesAccountAssignmentFilter(account, activeAccountFilter) &&
              matchesAccountSearch(account, searchQuery)
          );

        return { watchList, filteredAccounts };
      })
      .filter(group => group.filteredAccounts.length > 0);
  }, [watchListsData, activeAccountFilter, searchQuery]);

  // Keep selected account details for "Create Group" modal
  const selectedAccountsForGroup = useMemo(
    () =>
      accountsData.filter(account => selectedAccountIds.includes(account.id)),
    [accountsData, selectedAccountIds]
  );

  // Prevent duplicate group names (case-insensitive, trimmed)
  const normalizedGroupName = groupName.trim().toLowerCase();
  const isDuplicateGroupName = useMemo(
    () =>
      !!normalizedGroupName &&
      (watchListsData || []).some(
        watchList =>
          watchList.watch_list_name?.trim().toLowerCase() ===
          normalizedGroupName
      ),
    [watchListsData, normalizedGroupName]
  );

  // Helper function to get account detail fields for modal from ActiveCustomerWithSponsorsItem
  const getAccountDetailFields = (
    account: ActiveCustomerWithSponsorsItem | null
  ): DetailField[] => {
    if (!account) return [];

    // Use API fields directly, with fallbacks
    const industry = account.industry || "N/A";
    const location =
      account.location ||
      account.city ||
      account.country ||
      account.region ||
      "N/A";

    return [
      {
        label: "Name",
        value: account.name || "N/A",
      },
      {
        label: "Industry",
        value: industry,
      },
      {
        label: "Location",
        value: location,
      },
      {
        label: "Primary Sponsors",
        value: account.primary_sponsors || "N/A",
      },
      {
        label: "Secondary Sponsors",
        value: account.secondary_sponsors || "N/A",
      },
      {
        label: "Revenue",
        value: formatAccountRevenue(account.revenue),
      },
      {
        label: "Renewal",
        value: account.renewal
          ? formatDateTime(account.renewal).date
          : account.last_level_date
            ? formatDateTime(account.last_level_date).date
            : "-",
      },
      {
        label: "Tier",
        value: account.tiers || "N/A",
      },
    ];
  };

  // Helper function to get account detail fields from GroupedAccountData (for watch list accounts)
  const getGroupedAccountDetailFields = (
    account: GroupedAccountData
  ): DetailField[] => {
    return [
      {
        label: "Name",
        value: account.name || "N/A",
      },
      {
        label: "Industry",
        value: account.industry || "N/A",
      },
      {
        label: "Location",
        value: account.location || "N/A",
      },
      {
        label: "Primary Sponsors",
        value: account.primarySponsor || "N/A",
      },
      {
        label: "Secondary Sponsors",
        value: account.secondarySponsor || "N/A",
      },
      {
        label: "Revenue",
        value: formatAccountRevenue(account.revenue),
      },
      {
        label: "Renewal",
        value: account.renewal || "-",
      },
      {
        label: "Tier",
        value: account.tier || "N/A",
      },
    ];
  };

  // Render account card for accordion view
  const renderAccountCard = (account: GroupedAccountData) => {
    const handleMoreClick = () => {
      // Find the original account data from accountsDataRaw
      const originalAccount = accountsDataRaw.find(
        item => item.customer_id === account.id
      );
      if (originalAccount) {
        // Use the existing viewAccount state and getAccountDetailFields
        setViewAccount(originalAccount);
      } else {
        // For watch list accounts, use GroupedAccountData fields
        setAccountDetailsFields(getGroupedAccountDetailFields(account));
        setIsAccountDetailsModalOpen(true);
      }
    };

    return (
      <div className="account-card-grouped border-t">
        <AccountMetricsRow
          name={account.name}
          initials={getInitials(account.name)}
          tier={account.tier}
          industry={account.industry}
          location={account.location}
          primarySponsors={account.primarySponsors}
          secondarySponsors={account.secondarySponsors}
          revenue={account.revenue}
          renewal={account.renewal}
          rightActions={
            <>
              <Button
                variant="clear"
                className="action-button p-0"
                onClick={handleMoreClick}
              >
                <MoreVertical style={{ height: 24, width: 14 }} />
              </Button>
            </>
          }
        />
      </div>
    );
  };

  return (
    <div className="accounts-container">
      {/* Secondary Navigation Tabs */}
      <div className="flex items-center mb-4 justify-between">
        <TabsComponent
          tabs={accountFilterTabs}
          activeTab={activeAccountFilter || ""}
          onTabChange={handleAccountFilterChange}
          variant="tag-rounded"
        />
        <div className="flex gap-2 justify-end items-center">
          <DropdownSelector
            label="All Tiers"
            selected={selectedTier}
            options={[{ label: "All Tiers", value: "" }, ...tierOptions]}
            onSelect={setSelectedTier}
          />
          <DropdownSelector
            label="All Regions"
            selected={selectedRegion}
            options={[
              {
                label: "All Regions",
                value: null,
              },
              ...regionOptions,
            ]}
            onSelect={setSelectedRegion}
          />
          <DropdownSelector
            label="All Account Levels"
            selected={selectedLevel}
            options={levelOptions}
            onSelect={setSelectedLevel}
          />
          <div>
            <SearchInput
              placeholder="Search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="p-2 text-gray-500 hover:text-red-600 border-2 border-gray-300 rounded-full hover:border-red-400 transition-all duration-200"
            title="Clear all filters"
            onClick={handleClearFilters}
            aria-label="Clear search and filters"
          >
            <FilterX className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filters and Search Bar */}
      <div className="accounts-filters mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ToggleSwitch
              checked={showByGroup}
              onChange={setShowByGroup}
              label="Show By Group"
              id="show-by-group-toggle"
            />

            <Button
              variant="clear"
              className="fw-500 primary-color"
              onClick={handleCreateClick}
            >
              Create Group
            </Button>
          </div>
          <div className="accounts-export-toolbar flex items-center gap-3">
            <Button
              variant="outline-primary"
              onClick={handleOpenExportModal}
              icon={<Download size={16} strokeWidth={2} />}
            >
              Export
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setAccountToEdit(null);
                setIsAddAccountOpen(true);
              }}
            >
              <Plus size={18} className="mr-2" />
              Add Account
            </Button>
          </div>
        </div>
      </div>

      {/* Accounts Display - Accordion or Table */}
      {showByGroup ? (
        <div className="accounts-accordion-view">
          {/* Watch Lists Groups */}
          {watchListsLoading ? (
            <div className="flex flex-col items-center justify-center min-h-[400px] py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading watch lists...</p>
            </div>
          ) : filteredWatchListGroups.length > 0 ? (
            filteredWatchListGroups.map(({ watchList, filteredAccounts }) => (
              <Accordion
                key={watchList.watch_list_id}
                iconPosition="before"
                title={
                  <div className="flex justify-between items-center w-full gap-3">
                    <p className="text-start m-0">
                      {watchList.watch_list_name}
                    </p>
                    <div className="flex items-center gap-3 shrink-0">
                      <p className="text-sm text-gray-500 m-0">
                        {filteredAccounts.length} Accounts
                      </p>
                      <span
                        role="button"
                        tabIndex={0}
                        className="group-delete-btn"
                        aria-label={`Delete group ${watchList.watch_list_name}`}
                        title="Delete group"
                        onClick={event =>
                          handleDeleteGroupClick(event, watchList)
                        }
                        onKeyDown={event => {
                          if (event.key === "Enter" || event.key === " ") {
                            handleDeleteGroupClick(event, watchList);
                          }
                        }}
                      >
                        <Trash2 size={16} />
                      </span>
                    </div>
                  </div>
                }
                defaultOpen={false}
              >
                <div className="accounts-group-content">
                  {filteredAccounts.map(account => (
                    <div key={account.id}>{renderAccountCard(account)}</div>
                  ))}
                </div>
              </Accordion>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center min-h-[400px] py-8">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-base font-medium text-gray-600">
                {watchListsData && watchListsData.length > 0
                  ? "No accounts found for this filter"
                  : "No watch lists found"}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="accounts-table">
          {accountsLoading ? (
            <div className="flex flex-col items-center justify-center min-h-[400px] py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading accounts...</p>
            </div>
          ) : (
            <Table
              sortable
              className="accounts-table-scroll"
              columns={[
                {
                  key: "name",
                  title: "Name",
                  width: "240px",
                  sortable: true,
                  sortValue: (record: any) => record.name,
                  render: (_, record: any) => (
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-blue-600">
                            {getInitials(record.name)}
                          </span>
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {record.name}
                      </span>
                    </div>
                  ),
                  className: "whitespace-nowrap",
                },
                {
                  key: "region",
                  title: "Region",
                  width: "140px",
                  sortable: true,
                  sortValue: (record: any) => record.region,
                  render: (value: string) => <span>{value}</span>,
                  className: "whitespace-nowrap",
                },
                {
                  key: "industry",
                  title: "Industry & Location",
                  width: "240px",
                  sortable: true,
                  sortValue: (record: DisplayAccountData) =>
                    `${record.industry || ""} ${record.location || ""}`.trim(),
                  render: (_: any, record: DisplayAccountData) => {
                    const apiRecord = accountsDataRaw.find(
                      item => item.customer_id === record.id
                    );
                    const location = apiRecord?.location || record.location;
                    return (
                      <div className="flex flex-col gap-1">
                        <span>{record.industry}</span>
                        {location && location !== "N/A" && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <MapPin size={12} className="text-gray-400" />
                            <span>{location}</span>
                          </div>
                        )}
                      </div>
                    );
                  },
                  className: "whitespace-nowrap",
                },
                {
                  key: "primarySponsor",
                  title: "Primary Sponsors",
                  width: "200px",
                  sortable: true,
                  sortValue: (record: any) => record.primarySponsor,
                  className: "whitespace-nowrap",
                },
                {
                  key: "secondarySponsor",
                  title: "Secondary Sponsors",
                  width: "200px",
                  sortable: true,
                  sortValue: (record: any) => record.secondarySponsor,
                  className: "whitespace-nowrap",
                },
                {
                  key: "tier",
                  title: "Tiers",
                  width: "180px",
                  sortable: true,
                  sortValue: (record: any) => record.tier,
                  render: (value: string) => renderTierBadge(value),
                  className: "whitespace-nowrap",
                },
                {
                  key: "alignmentLevel",
                  title: "Account Level",
                  width: "150px",
                  sortable: true,
                  sortValue: (record: any) => record.alignmentLevel,
                  className: "whitespace-nowrap",
                },
                {
                  key: "revenue",
                  title: "Revenue",
                  width: "120px",
                  sortable: true,
                  sortValue: (record: any) => {
                    const raw = record.revenue;
                    if (raw == null) return null;
                    if (typeof raw === "number") return raw;
                    const s = String(raw).trim();
                    const m = s.match(/(-?\d+(\.\d+)?)/);
                    if (!m) return s;
                    let n = Number(m[1]);
                    if (/b/i.test(s)) n *= 1_000_000_000;
                    else if (/m/i.test(s)) n *= 1_000_000;
                    else if (/k/i.test(s)) n *= 1_000;
                    return n;
                  },

                  className: "whitespace-nowrap",
                },
                {
                  key: "renewal",
                  title: "Renewal",
                  width: "150px",
                  sortable: true,
                  sortValue: (record: DisplayAccountData) => {
                    const apiRecord = accountsDataRaw.find(
                      item => item.customer_id === record.id
                    );
                    const renewalDate =
                      apiRecord?.renewal || apiRecord?.last_level_date;
                    const d = renewalDate ? new Date(renewalDate) : null;
                    return d && !Number.isNaN(d.getTime())
                      ? d.getTime()
                      : record.renewal;
                  },
                  render: (_: any, record: DisplayAccountData) => {
                    return <span>{record.renewal}</span>;
                  },
                  className: "whitespace-nowrap",
                },
                {
                  key: "actions",
                  title: "Details",
                  width: "220px",
                  className: "whitespace-nowrap",
                  render: (_, record: DisplayAccountData) => (
                    <div className="flex justify-center items-center">
                      <Button
                        variant="clear"
                        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                        onClick={() => {
                          const apiRecord = accountsDataRaw.find(
                            item => item.customer_id === record.id
                          );
                          if (apiRecord) {
                            setAccountToEdit(apiRecord);
                            setIsAddAccountOpen(true);
                          }
                        }}
                        aria-label="Edit account"
                      >
                        <SquarePen size={18} className="text-blue-600" />
                      </Button>
                      <Button
                        variant="clear"
                        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                        onClick={() =>
                          navigate(withOrg(`/account-synopsis/${record.id}`), {
                            state: {
                              from: withOrg("/ops?tab=accounts"),
                              fromLabel: "Accounts",
                            },
                          })
                        }
                        aria-label="Open account synopsis"
                        title="Account Synopsis"
                      >
                        <FileText size={18} className="text-teal-600" />
                      </Button>
                      <Button
                        variant="clear"
                        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                        onClick={() =>
                          navigate(withOrg(`/account-workspace/${record.id}`), {
                            state: {
                              from: withOrg("/ops?tab=accounts"),
                              fromLabel: "Accounts",
                            },
                          })
                        }
                        aria-label="Open account workspace"
                      >
                        <ExternalLink size={18} className="text-violet-600" />
                      </Button>
                      <Button
                        variant="clear"
                        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                        onClick={() => handleDeleteClick(record)}
                        aria-label="Delete account"
                      >
                        <Trash2 size={18} className="text-red-600" />
                      </Button>
                      <Button
                        variant="clear"
                        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                        onClick={() => {
                          const apiRecord = accountsDataRaw.find(
                            item => item.customer_id === record.id
                          );
                          if (apiRecord) {
                            setViewAccount(apiRecord);
                          }
                        }}
                        aria-label="View account"
                      >
                        <MoreVertical
                          size={18}
                          strokeWidth={1}
                          className="text-gray-600"
                        />
                      </Button>
                    </div>
                  ),
                  textalign: "center",
                },
              ]}
              data={accountsData}
              scrollable={true}
              stickyFirstColumn
              maxHeight="calc(100vh - 20.25rem)"
            />
          )}
        </div>
      )}

      {/* Export Modal */}
      <CommonModal
        isOpen={isExportModalOpen}
        toggle={handleCloseExportModal}
        title="Export Accounts"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleCloseExportModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={accountsExportLoading}
              onClick={() => {
                void handleConfirmExport();
              }}
            >
              Export
            </Button>
          </div>
        }
      >
        <div className="accounts-export-modal">
          <div className="accounts-export-modal__fields">
            <Select
              label="Export Users"
              name="exportUsers"
              value={exportFilter}
              options={[
                { label: "All Users", value: "all" },
                {
                  label: "Executive Sponsors",
                  value: "executive_sponsors",
                },
              ]}
              onChange={e => {
                const value = e.target.value;
                if (value === "all" || value === "executive_sponsors") {
                  setExportFilter(value);
                }
              }}
              isShowPlaceHolder={false}
            />
            <Select
              label="Export as"
              name="exportFormat"
              value={exportFormat}
              options={[
                { label: "CSV", value: "csv" },
                { label: "Excel", value: "excel" },
              ]}
              onChange={e => {
                const value = e.target.value;
                if (value === "csv" || value === "excel") {
                  setExportFormat(value);
                }
              }}
              isShowPlaceHolder={false}
            />
          </div>
        </div>
      </CommonModal>

      {/* Create Group Modal */}
      <CommonModal
        isOpen={isCreateGroupModalOpen}
        toggle={handleBackFromModal}
        title="Create Group"
        size="2xl"
      >
        <div className="create-group-modal-content">
          {createGroupStep === 1 ? (
            <div className="mt-2">
              <CommonInput
                label="Group Name"
                name="groupName"
                type="text"
                value={groupName}
                placeholder="Enter Group Name"
                onChange={e => setGroupName(e.target.value)}
                required
                disabled={createWatchListLoading}
              />
              <p className="text-xs text-gray-500 mt-2 mb-0">
                Add a clear and unique group name before selecting accounts.
              </p>
              {isDuplicateGroupName && (
                <p className="text-red-600 text-sm mt-2 mb-0">
                  A group with this name already exists. Please use a different
                  name.
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4">
                <p className="text-sm font-medium m-0">
                  {selectedAccountIds.length} Accounts Selected
                </p>
              </div>
              <div className="border border-gray-200 rounded-lg p-3 max-h-72 overflow-auto bg-white">
                {accountsData.length > 0 && (
                  <div className="flex items-center justify-between border-b pb-2 mb-3">
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        Select accounts
                      </span>
                      <p className="text-xs text-gray-500 m-0 mt-1">
                        Choose one or more accounts for this group.
                      </p>
                    </div>
                    <CustomCheckbox
                      checked={
                        selectedAccountIds.length === accountsData.length &&
                        accountsData.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </div>
                )}
                {accountsData.length > 0 ? (
                  <div className="space-y-2">
                    {accountsData.map(account => (
                      <div
                        key={account.id}
                        className="flex items-center justify-between rounded-md px-2 py-2 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <CustomCheckbox
                            checked={selectedAccountIds.includes(account.id)}
                            onChange={() => handleAccountSelect(account.id)}
                          />
                          <InitialAvatar initials={account.initials} />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 m-0 truncate">
                              {account.name}
                            </p>
                            <p className="text-xs text-gray-500 m-0 truncate">
                              {account.location || "N/A"} • ARR:{" "}
                              {formatAccountRevenue(account.revenue)}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`inline-block px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${getTierBadgeClasses(account.tier)}`}
                        >
                          {formatTier(account.tier) || "N/A"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 m-0">
                    No accounts available with current filters.
                  </p>
                )}
              </div>
              {selectedAccountsForGroup.length === 0 && (
                <p className="text-sm text-amber-600 mt-2 mb-0">
                  Select at least one account to continue.
                </p>
              )}
            </>
          )}
          <div className="flex gap-2 justify-end mt-6">
            <Button
              variant="outline-primary"
              className="btn-lg"
              onClick={() =>
                createGroupStep === 1
                  ? handleBackFromModal()
                  : setCreateGroupStep(1)
              }
              disabled={createWatchListLoading}
            >
              Back
            </Button>
            {createGroupStep === 1 ? (
              <Button
                variant="primary"
                onClick={() => setCreateGroupStep(2)}
                disabled={
                  !groupName.trim() ||
                  isDuplicateGroupName ||
                  createWatchListLoading
                }
                className="btn-lg"
              >
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleSaveGroup}
                disabled={
                  !groupName.trim() ||
                  selectedAccountIds.length === 0 ||
                  isDuplicateGroupName ||
                  createWatchListLoading
                }
                className="btn-lg"
              >
                {createWatchListLoading ? "Creating..." : "Create Group"}
              </Button>
            )}
          </div>
        </div>
      </CommonModal>

      {/* Add Account Sidebar */}
      <SidebarModal
        isOpen={isAddAccountOpen}
        onClose={() => setIsAddAccountOpen(false)}
        title={accountToEdit ? "Edit Account" : "Add Multiple Accounts"}
        className="add-accounts-sidebar"
      >
        <AddAccount
          onClose={() => {
            setIsAddAccountOpen(false);
            setAccountToEdit(null);
          }}
          accountToEdit={accountToEdit}
        />
      </SidebarModal>

      {/* Account Details Modal */}
      <AccountsDetailsModal
        isOpen={!!viewAccount}
        onClose={() => setViewAccount(null)}
        title={`Account Details - ${viewAccount?.name || ""}`}
        fields={getAccountDetailFields(viewAccount)}
      />

      {/* Account Details Modal for Watch List Accounts */}
      <AccountsDetailsModal
        isOpen={isAccountDetailsModalOpen}
        onClose={() => {
          setIsAccountDetailsModalOpen(false);
          setAccountDetailsFields([]);
        }}
        title={`Account Details - ${
          accountDetailsFields.find(field => field.label === "Name")?.value ||
          ""
        }`}
        fields={accountDetailsFields}
      />

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        account={accountToDelete}
        onSuccess={handleDeleteSuccess}
      />

      {/* Delete Group Modal */}
      <DeleteModal
        isOpen={isDeleteGroupModalOpen}
        toggle={handleDeleteGroupModalClose}
        title="Delete Group"
        entityName={groupToDelete?.watch_list_name || "this group"}
        entityDetails={
          groupToDelete
            ? `${groupToDelete.customer_count} ${
                groupToDelete.customer_count === 1 ? "Account" : "Accounts"
              }`
            : undefined
        }
        onConfirm={handleDeleteGroupConfirm}
        loading={deleteWatchListLoading}
      />
    </div>
  );
};

export default AccountsContainer;
